import React, {
    createContext, useEffect, useReducer, useMemo
} from 'react';

const initialState = {
    instances: {},
    latestItemId: null,
    itemToCloseId: null,
    parentBounding: null,
}

const actions = {
    SET_IS_VISIBLE: 'SET_IS_VISIBLE',
    CLOSE_ORIGINATED_POPUP_INSTANCE: 'CLOSE_ORIGINATED_POPUP_INSTANCE',
    REMOVE_INSTANCE: 'REMOVE_INSTANCE',
    CREATE_ORIGINATED_POPUP_INSTANCE_BY_PARENT_POSITION: 'CREATE_ORIGINATED_POPUP_INSTANCE_BY_PARENT_POSITION',
    SET_ORIGINATED_POPUP_POSITION_AND_CREATE_NEW_ORIGINATED_POPUP_INSTANCE:
        'SET_ORIGINATED_POPUP_POSITION_AND_CREATE_NEW_ORIGINATED_POPUP_INSTANCE',
    SET_ORIGINATED_POPUP_ITEM: 'SET_ORIGINATED_POPUP_ITEM',
}

const reducer = (state, action) => {
    switch (action.type) {
        case actions.SET_IS_VISIBLE:
            return state.latestItemId ? {
                ...state,
                instances: {
                    ...state.instances,
                    [state.latestItemId]: { ...state.instances[state.latestItemId], isVisible: action.payload }
                }
            } : state;
        case actions.CLOSE_ORIGINATED_POPUP_INSTANCE:
            return {
                ...state,
                latestItemId: state.latestItemId === action.id ? null : state.latestItemId,
                itemToCloseId: action.id,
                instances: { ...state.instances, [action.id]: { ...state.instances[action.id], isVisible: false } }
            };
        case actions.REMOVE_INSTANCE:
            return {
                ...state,
                itemToCloseId: null,
                instances: Object.fromEntries(Object.entries(state.instances).filter(([key]) => key !== action.payload))
            }
        case actions.CREATE_ORIGINATED_POPUP_INSTANCE_BY_PARENT_POSITION:
            return {
                ...state,
                parentBounding: action.parentBounding,
                latestItemId: action.id
            };
        case actions.SET_ORIGINATED_POPUP_POSITION_AND_CREATE_NEW_ORIGINATED_POPUP_INSTANCE:
            return state.latestItemId ? {
                ...state,
                instances: {
                    ...state.instances,
                    [state.latestItemId]: {
                        selectedItem: null,
                        originatedPopupTop: action.originatedPopupTop,
                        originatedPopupLeft: action.originatedPopupLeft,
                        originatedPopupBottom: action.originatedPopupBottom,
                        originatedPopupRight: action.originatedPopupRight,
                        isVisible: false
                    }
                }
            } : state;
        case actions.SET_ORIGINATED_POPUP_ITEM:
            return state.latestItemId ? {
                ...state,
                instances: {
                    ...state.instances,
                    [state.latestItemId]: {
                        ...state.instances[state.latestItemId], selectedItem: action.payload
                    }
                }
            } : state;
        default:
            return state;
    }
}

export const OriginatedPopupContext = createContext();

const OriginatedPopupContextProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);


    useEffect(() => {
        state.parentBounding?.top && calculateAndSetOriginatedPopupPosition(state?.parentBounding);

        const repositionOriginatedPopupOnResize = () => {
            calculateAndSetOriginatedPopupPosition(state.parentBounding);
        }

        window.addEventListener('resize', repositionOriginatedPopupOnResize);
        return () => { window.removeEventListener('resize', repositionOriginatedPopupOnResize); }
    }, [state.parentBounding]);

    const calculateAndSetOriginatedPopupPosition = (parentBounding) => {
        // const pageWidth = window.innerWidth;
        const pageHeight = window.innerHeight;

        // calcuate vertical position
        let top;
        let bottom;
        let left;
        let right;

        const triggerElementHeight = (parentBounding?.bottom - parentBounding?.top);
        const verticalGap = 15;
        if (window.innerWidth <= 768) {
            bottom = 0;
        }
        else if (parentBounding?.top <= pageHeight / 2) {
            // if trigger element is in top half of the page
            parentBounding?.top && (top = parentBounding.top + triggerElementHeight + verticalGap);
        }
        else {
            // if trigger element is in bottom half of the page
            parentBounding?.bottom && (bottom = pageHeight -
                parentBounding.bottom + triggerElementHeight + verticalGap);

        }

        // calculate horizontal position
        if (parentBounding?.right <= 256 + 4 + 20) {
            // if it's left side of the page, render toward right
            left = parentBounding.left;
        }
        else {
            parentBounding?.right && (left = parentBounding.right - (256 + 4));
            // 256px = 16rem => width of the popup, 4px is an additional margin
        }

        dispatch({
            type: actions.SET_ORIGINATED_POPUP_POSITION_AND_CREATE_NEW_ORIGINATED_POPUP_INSTANCE,
            originatedPopupTop: top,
            originatedPopupBottom: bottom,
            originatedPopupRight: right,
            originatedPopupLeft: left
        });
    }

    // (transition related)
    useEffect(() => {
        const makeVisibleTimeout = setTimeout(() => {
            if (state?.instances?.[state?.latestItemId] &&
                (state?.instances?.[state?.latestItemId]?.isVisible === false)) {
                dispatch({ type: actions.SET_IS_VISIBLE, payload: true });
            }
        }, 100);
        return () => { clearTimeout(makeVisibleTimeout) }
    }, [state.instances, state.latestItemId]);


    // (transition related) to close with transition
    useEffect(() => {
        const makeVisibleTimeout = setTimeout(() => {
            if (state.itemToCloseId) {
                dispatch({ type: actions.REMOVE_INSTANCE, payload: state.itemToCloseId });
            }
        }, 100);
        return () => { clearTimeout(makeVisibleTimeout); }
    }, [state.itemToCloseId]);


    const value = useMemo(() => ({
        closeOriginatedPopupInstance: (id) => { dispatch({ type: actions.CLOSE_ORIGINATED_POPUP_INSTANCE, id }) },
        parentBounding: state.parentBounding,
        openOriginatedPopupByParentPosition: (parentBounding, id) => {
            dispatch({ type: actions.CREATE_ORIGINATED_POPUP_INSTANCE_BY_PARENT_POSITION, parentBounding, id })
        },
        instances: state.instances,
        latestItemId: state.latestItemId,
        // ⚠️ note: wait 10 ms to prevent from conflict on originatedPopup closeclick event listener,
        // (if we use it without setTimeout, then mean while it's removing the content to set second
        // stage's content, it checks existence of the clicked item in the container and as it's not
        // there, it will closes popup unexpectedly.)
        setSelectedItem: payload => {
            setTimeout(() => {
                dispatch({ type: actions.SET_ORIGINATED_POPUP_ITEM, payload })
            }, 10)
        },
    }), [state, dispatch]);

    return (
        <OriginatedPopupContext.Provider value={value}>
            {children}
        </OriginatedPopupContext.Provider>
    )
}

export default OriginatedPopupContextProvider;
